import { useMemo } from 'react';

import { Route, Switch, useLocation } from 'react-router-dom';

import { FormGrid } from 'components';
import { Navbar } from 'components/Navbar';
import { PrivateRoute } from 'components/PrivateRoute';
import { TimeoutController } from 'components/TimeoutModal/TimeoutController';
import { ReviewedCandidateProvider } from 'contexts';
import { getReturnButtonPath } from 'helpers';
import { useUserContext } from 'hooks/useUserContext';
import { SignerCandidateTable } from 'pages/dashboard/SignerCandidateTable';
import { SignerTrancheTable } from 'pages/dashboard/SignerTrancheTable';
import { SignerUserTable } from 'pages/dashboard/SignerUserTable';
import { NotFound } from 'pages/error/NotFound';
import { PermissionDenied } from 'pages/error/PermissionDenied';
import { ReviewedCandidatePage } from 'pages/reviewed_candidate/ReviewedCandidatePage';
import { SignerUserForm } from 'pages/user/SignerUserForm';
import { UserPage } from 'pages/user/UserPage';
import { UserProps } from 'types';

const createAuthChecks = (user: UserProps | null) => [
  {
    check: () => user?.role === 'signer',
    path: '/home/signer_dashboard/403',
  },
];

export const ContentWrapper = () => {
  const location = useLocation<{ from?: string }>();
  const { user, loaded } = useUserContext();
  const authChecks = useMemo(() => createAuthChecks(user), [user]);

  // This stops the auth checks in each route from firing before the user data is loaded
  if (!user && !loaded) {
    // Still loading
    return null;
  }

  return (
    <ReviewedCandidateProvider>
      <Navbar
        profile
        links={[
          { path: '/home/signer_dashboard/tranches', label: 'Tranches' },
          {
            path: '/home/signer_dashboard/reviewed_candidates',
            label: 'Candidates',
          },
          {
            path: '/home/signer_dashboard/users',
            label: 'Users',
          },
        ]}
      />
      <Switch>
        <PrivateRoute
          path='/home/signer_dashboard/reviewed_candidates/:candidate_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/signer_dashboard/reviewed_candidates',
                location.state?.from,
              )}
              label='RETURN TO CANDIDATES'
            >
              <ReviewedCandidatePage />
            </FormGrid>
          )}
          // exact
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/signer_dashboard/reviewed_candidates'
          component={SignerCandidateTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/signer_dashboard/tranches'
          component={SignerTrancheTable}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/signer_dashboard/me'
          component={() => (
            <FormGrid
              maxWidth='sm'
              path={getReturnButtonPath(
                '/home/signer_dashboard/reviewed_candidates',
                location.state?.from,
              )}
              label='RETURN TO CANDIDATES'
            >
              <SignerUserForm user={user as UserProps} />
            </FormGrid>
          )}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/signer_dashboard/users/:user_id'
          component={() => (
            <FormGrid
              path={getReturnButtonPath(
                '/home/signer_dashboard/users',
                location.state?.from,
              )}
              label='RETURN TO USERS'
            >
              <UserPage />
            </FormGrid>
          )}
          authChecks={authChecks}
        />
        <PrivateRoute
          path='/home/signer_dashboard/users'
          component={SignerUserTable}
          authChecks={authChecks}
        />
        <Route path='/home/signer_dashboard/403' component={PermissionDenied} />
        <Route component={NotFound} />
      </Switch>
      <TimeoutController />
    </ReviewedCandidateProvider>
  );
};
